import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'

interface ScrollStage<T extends HTMLElement> {
  /** Attach to the element whose scroll-out drives the effect. */
  ref: RefObject<T>
}

/**
 * Publishes an element's scroll-out progress as a `--progress` custom
 * property: 0 while it is fully in view, 1 once it has scrolled away.
 *
 * The travel is the element's height minus one viewport — exactly how long
 * its `position: sticky` child stays pinned. Deriving it from the element
 * rather than hardcoding a distance means the fade and the pin can never
 * drift apart, whatever height the runway is given.
 *
 * Writes a CSS variable inside a rAF rather than setting React state: a
 * scroll would otherwise re-render the tree every frame. The value is
 * quantised so identical frames cost no style recalculation.
 *
 * Both the value and a coarse stage are published on `<html>` — as
 * `--intro-progress` and `data-intro` — so the header and the page content
 * can react in CSS without anything being passed down the tree.
 */
export const useScrollStage = <T extends HTMLElement = HTMLElement>(): ScrollStage<T> => {
  const ref = useRef<T>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const root = document.documentElement
    let frame = 0
    let last = -1

    const read = () => {
      frame = 0
      const top = el.offsetTop
      const travel = Math.max(1, el.offsetHeight - window.innerHeight)
      const progress = Math.min(1, Math.max(0, (window.scrollY - top) / travel))
      const rounded = Math.round(progress * 1000) / 1000
      if (rounded === last) return
      last = rounded
      root.style.setProperty('--intro-progress', String(rounded))
      root.dataset.intro = rounded > 0.8 ? 'done' : 'active'
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(read)
    }

    read()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      root.style.removeProperty('--intro-progress')
      delete root.dataset.intro
    }
  }, [])

  return { ref }
}
